import { useAction } from '@reatom/npm-react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { Dialog } from '@root/shared/ui-kit'

import { downloadUpdateAction } from '../../atoms'

import * as Markdown from './markdown'

import type { DialogControlProps } from '@root/shared/ui-kit'

export type UpdateAnnouncementDialogProps = {
  version: string
  notesMD: string
} & DialogControlProps

export const UpdateAnnouncementDialog = (
  props: UpdateAnnouncementDialogProps,
) => {
  const { t } = useTranslation()
  const downloadUpdate = useAction(downloadUpdateAction)

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      describedBy={t('update.update_available')}
    >
      <Main>
        <Header>
          <HeaderBG />
          <Version>{props.version}</Version>
        </Header>
        <Body>
          <Markdown.Markdown
            fallback={<p>{t('common.downloading') + '...'}</p>}
          >
            {props.notesMD}
          </Markdown.Markdown>
        </Body>
        <Footer>
          <Button
            variant={'soft'}
            radius={'rounded'}
            fullWidth
            onClick={() => props.onOpenChange?.(false)}
          >
            {t('common.install')} {t('common.later')}
          </Button>
          <Button
            variant={'solid'}
            color={'primary'}
            fullWidth
            radius={'rounded'}
            onClick={() => {
              props.onOpenChange?.(false)
              downloadUpdate()
            }}
          >
            {t('common.install')} {t('common.update')}
          </Button>
        </Footer>
      </Main>
    </Dialog>
  )
}
const Main = styled.div``

const Header = styled.div`
  height: 120px;
  width: 600px;
  background: var(--appearance-1);
  position: relative;
  overflow: hidden;
`

const HeaderBG = styled.div`
  position: absolute;
  border-radius: 50%;
  margin: -500px;
  left: 50%;
  top: -1000px;

  width: 1000px;
  height: 2000px;
  background: var(--appearance-1);
  background: radial-gradient(
    rgba(165, 183, 255, 1) 32%,
    rgba(64, 101, 255, 1) 37%,
    rgba(7, 67, 195, 1) 40%,
    black 62%
  );
`

const Version = styled.div`
  bottom: -25px;
  line-height: normal;
  position: absolute;
  width: 100%;
  text-align: center;

  font-weight: 700;
  font-size: 100px;

  font-weight: bold;
  color: transparent;

  background: linear-gradient(
    rgba(255, 255, 255, 0.2) 0,
    rgba(255, 255, 255, 0) 80%,
    rgba(255, 255, 255, 0) 100%
  );
  -webkit-background-clip: text;

  filter: blur(0.1px);
`
const Footer = styled.div`
  display: flex;
  padding: 0 20px 20px;
  gap: 20px;
`
const Body = styled.div`
  padding: 20px;
  width: 600px;

  font-size: 14px;
  color: var(--text-10);

  p,
  ul,
  ol {
    font-size: inherit;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    text-wrap: balance;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  b,
  strong {
    color: var(--text-12);
  }

  a {
    color: var(--primary-9);
  }
  a:hover {
    color: var(--primary-10);
  }
  a:active,
  a:focus {
    color: var(--primary-11);
  }

  ol,
  ul {
    padding: 0 0 0 1.5ch;
    list-style: none;
  }

  li::before {
    content: '-';
    color: var(--purple-12);
    display: inline-block;
    margin-left: -1.5ch;
    padding-right: 0.5ch;
  }
  ol {
    counter-reset: md-counter;
  }
  ol li {
    counter-increment: md-counter;
  }
  ol li::before {
    content: counter(md-counter) '.';
  }

  blockquote {
    padding: 1ch 0 1ch 1.5ch;
    border-left: 2px solid var(--purple-12);
    font-size: 16px;
  }

  img {
    max-width: 100%;
  }

  > * + * {
    margin: 10px 0 0;
  }
`
